/**
 * Bookmarks Check Service
 * -------------------------
 * This service provides functions for working with bookmarks data,
 * including counting, checking limits, and determining subscription status.
 */

// Import the subscription check service for consistent premium status checking
import { isPremiumSubscription } from './subscription-check-service.js';

// Debug toggle: set to false to disable console logs in production
const DEBUG = false;
function dbgLog(...args) { if (DEBUG) console.log(...args); }
function dbgWarn(...args) { if (DEBUG) console.warn(...args); }

// Maximum number of bookmarks allowed for free plan users
export const FREE_PLAN_BOOKMARK_LIMIT = 10;

// Number of bookmarks before showing approaching limit warning
export const APPROACHING_LIMIT_THRESHOLD = 2;

/**
 * Check if the user has reached the free plan bookmark limit
 * @param {Array} bookmarks - The array of bookmarks
 * @param {number} limit - The maximum number of bookmarks for free plan (default: FREE_PLAN_BOOKMARK_LIMIT)
 * @returns {boolean} True if the limit is reached, false otherwise
 */
export function hasReachedBookmarkLimit(bookmarks, limit = FREE_PLAN_BOOKMARK_LIMIT) {
  const bookmarkCount = bookmarks?.length || 0;
  const isPremium = isPremiumSubscription();
  
  dbgLog(`[BookmarkLimitCheck] bookmarkCount=${bookmarkCount}, limit=${limit}, check result: isPremium=${isPremium}`);
  
  // Premium users have no limit
  if (isPremium) {
    dbgLog('[BookmarkLimitCheck] User is premium, no limit applies');
    return false;
  }
  
  // Free users are limited
  const hasReached = bookmarkCount >= limit;
  dbgLog(`[BookmarkLimitCheck] Free user check: ${bookmarkCount} >= ${limit} = ${hasReached}`);
  return hasReached;
}

/**
 * Check if the user is approaching the free plan bookmark limit
 * @param {Array} bookmarks - The array of bookmarks
 * @param {number} threshold - How many bookmarks before warning (default: APPROACHING_LIMIT_THRESHOLD)
 * @param {number} limit - The maximum number of bookmarks for free plan (default: FREE_PLAN_BOOKMARK_LIMIT)
 * @returns {boolean} True if approaching the limit, false otherwise
 */
export function isApproachingBookmarkLimit(bookmarks, threshold = APPROACHING_LIMIT_THRESHOLD, limit = FREE_PLAN_BOOKMARK_LIMIT) {
  const bookmarkCount = bookmarks?.length || 0;
  const isPremium = isPremiumSubscription();
  
  dbgLog(`[ApproachingLimitCheck] bookmarkCount=${bookmarkCount}, limit=${limit}, threshold=${threshold}, isPremium=${isPremium}`);
  
  // Premium users have no limit
  if (isPremium) {
    dbgLog('[ApproachingLimitCheck] User is premium, no limit applies');
    return false;
  }
  
  // Check if within threshold of limit
  const isApproaching = bookmarkCount >= (limit - threshold) && bookmarkCount < limit;
  dbgLog(`[ApproachingLimitCheck] Free user check: ${bookmarkCount} >= (${limit} - ${threshold}) && ${bookmarkCount} < ${limit} = ${isApproaching}`);
  return isApproaching;
}

/**
 * Get remaining bookmarks for free plan users
 * @param {Array} bookmarks - The array of bookmarks
 * @param {number} limit - The maximum number of bookmarks for free plan (default: FREE_PLAN_BOOKMARK_LIMIT)
 * @returns {number|null} Number of remaining bookmarks (0 if limit reached), null for premium users
 */
export function getRemainingBookmarks(bookmarks, limit = FREE_PLAN_BOOKMARK_LIMIT) {
  const isPremium = isPremiumSubscription();
  
  // Premium users have unlimited bookmarks
  if (isPremium) {
    return null;
  }
  
  const bookmarkCount = bookmarks?.length || 0;
  return Math.max(0, limit - bookmarkCount);
}

/**
 * Get the bookmarks to display based on user's subscription status
 * @param {Array} bookmarks - The full array of bookmarks
 * @param {number} limit - The maximum number of bookmarks for free plan (default: FREE_PLAN_BOOKMARK_LIMIT)
 * @returns {Object} Object containing displayedBookmarks and limitReached flag
 */
export function getVisibleBookmarks(bookmarks, limit = FREE_PLAN_BOOKMARK_LIMIT) {
  const isPremium = isPremiumSubscription();
  const hasReached = hasReachedBookmarkLimit(bookmarks, limit);
  
  dbgLog(`[GetVisibleBookmarks] isPremium=${isPremium}, hasReachedLimit=${hasReached}, totalBookmarks=${bookmarks?.length || 0}`);
  
  // Determine which bookmarks to display
  let displayedBookmarks = bookmarks || [];
  
  // For free users who have reached the limit, only show the first N bookmarks
  if (!isPremium && hasReached && displayedBookmarks.length > limit) {
    displayedBookmarks = displayedBookmarks.slice(0, limit);
    dbgLog(`[GetVisibleBookmarks] Limiting display to ${limit} bookmarks for free user`);
  }
  
  return {
    displayedBookmarks,
    limitReached: !isPremium && hasReached && bookmarks?.length > limit
  };
}

// Export as default object for easier importing
const bookmarksCheckService = {
  FREE_PLAN_BOOKMARK_LIMIT,
  APPROACHING_LIMIT_THRESHOLD,
  hasReachedBookmarkLimit,
  isApproachingBookmarkLimit,
  getRemainingBookmarks,
  getVisibleBookmarks
};

export default bookmarksCheckService;
